import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "./api/api";

function InvoiceMappingAlloted() {
  const location = useLocation();
  const navigate = useNavigate();
  const { FGitemId, ReqMapQty, CPId, returnDate } = location.state || {};
  //const user = JSON.parse(localStorage.getItem("user") || "{}");
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  
  const username = user.UsrName || "";

  const [allotedData, setAllotedData] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [invalidRows, setInvalidRows] = useState({});
  const [totalQty, setTotalQty] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState(null);

  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");

  const normalizeInputStr = (raw) => {
    let s = String(raw || "");
    s = s.replace(/\D/g, "");
    s = s.replace(/^0+/, "");
    if (s === "") return "0";
    return s;
  };

  const recomputeValidation = (updatedQuantities, data = allotedData) => {
    const req = Number(ReqMapQty || 0);
    const invalid = {};
    let total = 0;

    for (const row of data) {
      const jc = row.JCNo;
      const curr = Number(updatedQuantities[jc] || 0);
      total += curr;
      if (curr > Number(row.BalQty || 0)) {
        invalid[jc] = true;
      }
    }

    setInvalidRows(invalid);
    setTotalQty(total);
    setIsValid(Object.keys(invalid).length === 0 && total === req);
  };

  useEffect(() => {
    const fetchAllotedData = async () => {
      if (!username || !FGitemId) {
        setError("User information or Item ID is missing.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await api.JCInvoiceMappingavailableList(username, FGitemId);
        const dataWithBalQty = (res.Table || []).map((row) => ({
          ...row,
          BalQty: Number(row.BalQty ?? row.AllotedQty ?? 0),
        }));

        const initialQuantities = dataWithBalQty.reduce((acc, row) => {
          acc[row.JCNo] = 0;
          return acc;
        }, {});

        setAllotedData(dataWithBalQty);
        setQuantities(initialQuantities);
        recomputeValidation(initialQuantities, dataWithBalQty);
      } catch (err) {
        console.error("Error fetching available mapping list:", err);
        setError("Failed to load available mapping data.");
        setAllotedData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllotedData();
  }, [username, FGitemId, CPId]);

  const handleNavigateBack = () => {
    navigate("/InvoiceMapping", {
      state: {
        refresh: true,
        selectedDate: returnDate,
      },
    });
  };

  const handleBack = () => handleNavigateBack();

  const handleQtyChange = (jcNo, rawValue) => {
    const normalizedStr = normalizeInputStr(rawValue);
    const numeric = Number(normalizedStr);

    const updated = { ...quantities, [jcNo]: numeric };
    setQuantities(updated);
    recomputeValidation(updated);
  };

  const showMessage = (msg, type = "success") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage(null);
    }, 10000);
  };

  const handleSubmit = async () => {
    if (!isValid) {
      showMessage(
        "Validation failed. Fix highlighted fields and ensure total equals required quantity.",
        "error"
      );
      return;
    }

    setIsSubmitting(true);

    const items = allotedData
      .map((row) => ({
        Slno1: row.slno ?? null,
        JCId: row.Jcid ?? null,
        JCNo: row.JCNo ?? "",
        JCDate: row.JCDate ? row.JCDate.split("-").reverse().join("-") : null,
        ItemId: row.FGITemId ?? null,
        JCPartNo: row.JCPartNo ?? "",
        JCPartDesc: row.JCPartDesc ?? "",
        R2dQty: row.R2dQty ?? 0,
        AllotedQty: row.AllotedQty ?? 0,
        BalQty: Number(row.BalQty) - Number(quantities[row.JCNo] || 0),
        CurrQty: Number(quantities[row.JCNo] || 0),
        Cpid: CPId ?? row.Cpid ?? null,
        Username: username,
      }))
      .filter((r) => r.CurrQty > 0);

    const payload = { Items: items };

    try {
      const res = await api.submitInvoiceMappingAlloted(payload);

      if (res?.Success || res?.success) {
        showMessage("Mapping submitted successfully!", "success");

        setTimeout(() => {
          handleNavigateBack();
        }, 1000);
      } else {
        showMessage(res?.Message || res?.message || "Submission failed!", "error");
      }
    } catch (err) {
      console.error("API Error:", err);
      showMessage("Submission failed! Server error.", "error");
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 1000);
    }
  };

  const columnsToHide = ["Jcid", "FGITemId"];
  const finalHeaders = allotedData[0]
    ? [...Object.keys(allotedData[0]).filter((header) => !columnsToHide.includes(header)), "Current Qty"]
    : [];

  const hasInvalidRows = Object.keys(invalidRows).length > 0;
  const reqQtyNum = Number(ReqMapQty || 0);
  const totalMismatch = totalQty !== reqQtyNum;

  return (
    <div className="w-full h-screen p-4 bg-gray-50 flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-base font-semibold text-gray-800">
          Available Mapping Jobcard List
          <span className="ml-2 text-xs font-normal text-gray-600">
            (Required Qty:{" "}
            <span className="font-bold text-blue-600">{ReqMapQty ?? "N/A"}</span>)
          </span>
        </h1>

        <div className="flex items-end gap-2">
          <div className="text-xs text-gray-600 mr-4">
            <div>
              Current Total: <span className="font-semibold">{totalQty}</span>
            </div>
          </div>

          <button
            onClick={handleBack}
            className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded-md shadow-sm"
          >
            Back
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !isValid}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`text-xs px-2 py-1 rounded mb-2 ${messageType === "success"
            ? "bg-green-100 text-green-700"
            : "bg-red-100 text-red-700"
            }`}
        >
          {message}
        </div>
      )}

      <div className="mb-2">
        {hasInvalidRows && (
          <div className="text-sm text-red-600">
            Some entries exceed their Balance Qty — correct the highlighted fields.
          </div>
        )}
        {!hasInvalidRows && totalMismatch && (
          <div className="text-sm text-red-600">
            Total Current Qty must equal Required Qty ({reqQtyNum}). Current total: {totalQty}
          </div>
        )}
        {!hasInvalidRows && !totalMismatch && (
          <div className="text-sm text-green-700">All validations OK — you can submit.</div>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-gray-600 font-medium flex-1 flex items-center justify-center">
          Loading...
        </p>
      ) : error ? (
        <p className="text-xs text-red-600 font-medium flex-1 flex items-center justify-center">{error}</p>
      ) : allotedData.length === 0 ? (
        <p className="text-xs italic text-gray-500 flex-1 flex items-center justify-center">No data found.</p>
      ) : (
        <div className="flex-1 overflow-auto border border-gray-300 rounded-lg shadow-inner max-h-[calc(100vh-140px)]">
          <table className="w-full min-w-[700px] text-xs border-collapse">
            <thead>
              <tr className="bg-gray-200 text-gray-700 sticky top-0 z-10">
                {finalHeaders.map((h) => (
                  <th
                    key={h}
                    className="px-2 py-1 text-left font-medium border-b border-gray-300 whitespace-nowrap text-xs"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {allotedData.map((row) => (
                <tr key={row.JCNo} className="hover:bg-gray-50">
                  {finalHeaders.map((h) =>
                    h === "Current Qty" ? (
                      <td key={h} className="px-2 py-1 border-b border-gray-200 whitespace-nowrap">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="\d*"
                          value={String(quantities[row.JCNo] ?? 0)}
                          onChange={(e) => handleQtyChange(row.JCNo, e.target.value)}
                          className={`w-20 border rounded text-xs px-2 py-1 focus:outline-none 
                            ${invalidRows[row.JCNo]
                              ? "border-red-500 focus:ring-red-500 ring-1"
                              : "border-gray-300 focus:ring-blue-500"
                            }`}
                        />
                      </td>
                    ) : (
                      <td key={h} className="px-2 py-1 border-b border-gray-200 whitespace-nowrap text-xs">
                        {row[h] ?? "-"}
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default InvoiceMappingAlloted;
